"use client";

import React from "react";
import { motion } from "framer-motion";
import { Star, Briefcase, Award, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface ExpertPro {
  id: string;
  name: string;
  specialty: string;
  focus: string;
  badge: string;
  image: string;
  imageSrcset: string;
  imageSizes: string;
}
const experts: ExpertPro[] = [{
  id: "exp-1",
  name: "David Chen",
  specialty: "HVAC Specialist",
  focus: "AC & Heat Pumps",
  badge: "Independent Pro",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_david.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_david.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_david.png-wvc-sizes"
}, {
  id: "exp-2",
  name: "Sarah Jenkins",
  specialty: "AC Specialist",
  focus: "Cooling & Airflow",
  badge: "Independent Pro",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_sarah.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_sarah.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_sarah.png-wvc-sizes"
}, {
  id: "exp-3",
  name: "Marcus Johnson",
  specialty: "Heating Systems Specialist",
  focus: "Furnaces & Heat Pumps",
  badge: "Independent Pro",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_marcus.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_marcus.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_marcus.png-wvc-sizes"
}, {
  id: "exp-4",
  name: "Elena Rodriguez",
  specialty: "HVAC Diagnostic Specialist",
  focus: "Diagnostics & Tune-Ups",
  badge: "Independent Pro",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_elena.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_elena.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_expert_elena.webp-wvc-sizes"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
export default function ExpertTechnicians() {
  return <section id="experttechnicians" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-center text-center md:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.8
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4">
              {"Local HVAC Network"}
            </Badge>
            <h2 className="font-default text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
              {"Independent HVAC Pros"}
            </h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              {"Connect with experienced independent heating and cooling professionals in the Atlanta area who join through network onboarding."}
            </p>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {experts.map((expert, i) => <motion.div key={expert.id} variants={itemVariants} data-index={i}>
              <Card className="group flex h-full flex-col overflow-hidden border-transparent shadow-md transition-all hover:border-border hover:shadow-lg">
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  <img src={expert.image} data-wvc-srcset={expert.imageSrcset} data-wvc-sizes={expert.imageSizes} alt={`HVAC pro ${expert.name}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="flex items-center gap-1 shadow-sm">
                      <Award className="h-3 w-3" />
                      {expert.badge}
                    </Badge>
                  </div>
                </div>
                
                <CardHeader className="pb-2 pt-5">
                  <h3 className="font-default text-foreground text-xl font-semibold leading-tight">
                    {expert.name}
                  </h3>
                  <p className="text-muted-foreground text-sm font-medium">
                    {expert.specialty}
                  </p>
                </CardHeader>
                
                <CardContent className="mt-auto flex flex-col gap-4 pb-6">
                  <div className="flex items-center justify-between rounded-lg bg-accent/50 p-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-default text-foreground text-sm font-semibold">
                        {expert.focus}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        Atlanta Metro Area
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="mt-12 flex justify-center md:mt-16">
          <Button asChild size="lg" className="group">
            <Link to="#bookingrequest">
              {"Request a Quote"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}